import { useCallback, useEffect, useRef, useState } from "react";
import { serverNow } from "./clock";

/** Current time on the server's clock (see ./clock), refreshed every `intervalMs`. */
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    const t = window.setInterval(() => setNow(serverNow()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Milliseconds remaining until `iso` (never negative). */
export function useRemaining(iso: string | null | undefined): number {
  const now = useNow();
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, t - now);
}

export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} · UnizPay` : "UnizPay";
  }, [title]);
}

/** Returns a stable function that calls `fn` once calls stop for `ms`. Pending calls are dropped on unmount. */
export function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, ms: number) {
  const latest = useRef(fn);
  latest.current = fn;
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return useCallback(
    (...args: A) => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => latest.current(...args), ms);
    },
    [ms],
  );
}
